<template>
    <div>
        <button
            name="ship"
            class="w-full inline-flex justify-center items-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
            @click="openModal"
        >
            <CheckIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            {{ !!reship ? "Reship" : "Ship" }}
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <TruckIcon
                                        class="h-6 w-6 text-nutreance-green"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        {{ !!reship ? "Reship" : "Ship" }} Order
                                        #{{ publicOrderId }}
                                    </DialogTitle>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />
                                    <input
                                        v-if="!!reship"
                                        type="hidden"
                                        name="_method"
                                        value="PATCH"
                                    />

                                    <div class="sm:col-span-2 sm:px-16 pb-6">
                                        <div class="py-2">
                                            <label
                                                for="shipping_method_id"
                                                class="block text-sm font-medium text-gray-700"
                                            >
                                                Shipping Method
                                            </label>

                                            <div
                                                class="mt-1 relative rounded-md shadow-sm"
                                            >
                                                <select
                                                    v-model="
                                                        inputs.shipping_method_id
                                                    "
                                                    :ref="
                                                        refs.shipping_method_id
                                                    "
                                                    id="shipping_method_id"
                                                    name="shipping_method_id"
                                                    required
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'shipping_method_id',
                                                        )
                                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                        'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                                    ]"
                                                    :aria-invalid="
                                                        hasError(
                                                            errors,
                                                            'shipping_method_id',
                                                        )
                                                            ? 'true'
                                                            : 'false'
                                                    "
                                                    :aria-describedby="
                                                        hasError(
                                                            errors,
                                                            'shipping_method_id',
                                                        )
                                                            ? 'shipping_method_id-error'
                                                            : ''
                                                    "
                                                >
                                                    <option
                                                        v-for="(
                                                            shippingMethod,
                                                            shippingMethodId
                                                        ) in shippingMethods"
                                                        :key="shippingMethodId"
                                                        :value="
                                                            shippingMethodId
                                                        "
                                                    >
                                                        {{
                                                            shippingMethod.carrier
                                                        }}
                                                        {{
                                                            shippingMethod.service
                                                        }}
                                                    </option>
                                                </select>
                                                <div
                                                    v-if="
                                                        hasError(
                                                            errors,
                                                            'shipping_method_id',
                                                        )
                                                    "
                                                    class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                                                >
                                                    <ExclamationCircleIcon
                                                        class="h-5 w-5 text-red-500"
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                            </div>
                                            <p
                                                v-if="
                                                    hasError(
                                                        errors,
                                                        'shipping_method_id',
                                                    )
                                                "
                                                class="mt-2 text-sm text-red-600"
                                                id="shipping_method_id-error"
                                            >
                                                {{
                                                    error(
                                                        errors,
                                                        "shipping_method_id",
                                                    )
                                                }}
                                            </p>
                                        </div>

                                        <div v-if="!!reship" class="py-2">
                                            <label
                                                class="block text-sm text-center font-medium text-gray-700"
                                            >
                                                Refund Current Label
                                            </label>
                                            <input
                                                type="hidden"
                                                name="refund"
                                                :value="
                                                    inputs.refund ? '1' : ''
                                                "
                                            />
                                            <SwitchGroup
                                                as="li"
                                                class="pt-3 pb-1 flex items-center justify-center text-transparent"
                                            >
                                                <Switch
                                                    data-test="refund"
                                                    name="refund"
                                                    v-model="inputs.refund"
                                                    :class="[
                                                        inputs.refund
                                                            ? 'bg-nutreance-green'
                                                            : 'bg-gray-200',
                                                        'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                                                    ]"
                                                >
                                                    <span class="sr-only"
                                                        >Toggle Refund</span
                                                    >
                                                    <span
                                                        aria-hidden="true"
                                                        :class="[
                                                            inputs.refund
                                                                ? 'translate-x-5'
                                                                : 'translate-x-0',
                                                            'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                        ]"
                                                    />
                                                </Switch>
                                            </SwitchGroup>
                                        </div>

                                        <div class="py-2">
                                            <label
                                                class="block text-sm text-center font-medium text-gray-700"
                                            >
                                                Verify Address
                                            </label>
                                            <input
                                                type="hidden"
                                                name="verify"
                                                :value="
                                                    inputs.verify ? '1' : ''
                                                "
                                            />
                                            <SwitchGroup
                                                as="li"
                                                class="pt-3 pb-1 flex items-center justify-center text-transparent"
                                            >
                                                <Switch
                                                    data-test="verify"
                                                    name="verify"
                                                    v-model="inputs.verify"
                                                    :class="[
                                                        inputs.verify
                                                            ? 'bg-nutreance-green'
                                                            : 'bg-gray-200',
                                                        'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                                                    ]"
                                                >
                                                    <span class="sr-only"
                                                        >Toggle Verify</span
                                                    >
                                                    <span
                                                        aria-hidden="true"
                                                        :class="[
                                                            inputs.verify
                                                                ? 'translate-x-5'
                                                                : 'translate-x-0',
                                                            'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                        ]"
                                                    />
                                                </Switch>
                                            </SwitchGroup>
                                        </div>

                                        <div class="py-2">
                                            <label
                                                class="block text-sm text-center font-medium text-gray-700"
                                            >
                                                Require Signature
                                            </label>
                                            <input
                                                type="hidden"
                                                name="signature"
                                                :value="
                                                    inputs.signature ? '1' : ''
                                                "
                                            />
                                            <SwitchGroup
                                                as="li"
                                                class="pt-3 pb-1 flex items-center justify-center text-transparent"
                                            >
                                                <Switch
                                                    data-test="signature"
                                                    name="signature"
                                                    v-model="inputs.signature"
                                                    :class="[
                                                        inputs.signature
                                                            ? 'bg-nutreance-green'
                                                            : 'bg-gray-200',
                                                        'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                                                    ]"
                                                >
                                                    <span class="sr-only"
                                                        >Toggle Signature</span
                                                    >
                                                    <span
                                                        aria-hidden="true"
                                                        :class="[
                                                            inputs.signature
                                                                ? 'translate-x-5'
                                                                : 'translate-x-0',
                                                            'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                        ]"
                                                    />
                                                </Switch>
                                            </SwitchGroup>
                                        </div>

                                        <div
                                            v-if="hasUnresolvedIssues"
                                            class="py-2"
                                        >
                                            <label
                                                class="block text-sm text-center font-medium text-gray-700"
                                            >
                                                Issues Resolved
                                            </label>
                                            <input
                                                type="hidden"
                                                name="resolved"
                                                :value="
                                                    inputs.resolved ? '1' : ''
                                                "
                                            />
                                            <SwitchGroup
                                                as="li"
                                                class="pt-3 pb-1 flex items-center justify-center text-transparent"
                                            >
                                                <Switch
                                                    data-test="resolved"
                                                    name="resolved"
                                                    v-model="inputs.resolved"
                                                    :class="[
                                                        inputs.resolved
                                                            ? 'bg-nutreance-green'
                                                            : 'bg-gray-200',
                                                        'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                                                    ]"
                                                >
                                                    <span class="sr-only"
                                                        >Toggle Issues
                                                        Resolved</span
                                                    >
                                                    <span
                                                        aria-hidden="true"
                                                        :class="[
                                                            inputs.resolved
                                                                ? 'translate-x-5'
                                                                : 'translate-x-0',
                                                            'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                        ]"
                                                    />
                                                </Switch>
                                            </SwitchGroup>
                                        </div>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        {{ !!reship ? "Reship" : "Ship" }}
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    Switch,
    SwitchGroup,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    ExclamationCircleIcon,
    TruckIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";

const props = defineProps<{
    csrf?: string;
    defaultShippingMethodId?: string;
    errors?: any;
    hasUnresolvedIssues?: string;
    old?: any;
    publicOrderId?: string;
    reship?: string;
    shippingMethods?: any;
}>();

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, "shipping_method_id"),
);
const actionUrl = "/user/orders/" + props.publicOrderId + "/ship";

const refs = {
    shipping_method_id: ref<any>(null),
};

const inputs = reactive({
    shipping_method_id: oldInput(props.old, "shipping_method_id")
        ? oldInput(props.old, "shipping_method_id")
        : props.defaultShippingMethodId,
    refund: !!(Object.prototype.hasOwnProperty.call(props.old, "refund")
        ? oldInput(props.old, "refund")
        : false),
    verify: !!(Object.prototype.hasOwnProperty.call(props.old, "verify")
        ? oldInput(props.old, "verify")
        : true),
    signature: !!(Object.prototype.hasOwnProperty.call(props.old, "signature")
        ? oldInput(props.old, "signature")
        : false),
    resolved: !!(Object.prototype.hasOwnProperty.call(props.old, "resolved")
        ? oldInput(props.old, "resolved")
        : false),
});

function openModal() {
    open.value = true;

    nextTick(() => refs.shipping_method_id.value.focus());
}

onMounted(() => {
    if (open.value) {
        openModal();
    }
});
</script>
